import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import api from '@/lib/api'
import { IS_MOCK_MODE, STORAGE_KEYS, readJSON, writeJSON } from '@/lib/mockMode'
import { getDemoDocuments, pickSampleForFile } from '@/data/sampleDocuments'
import { trackEvent } from '@/utils/analytics'

const POLL_INTERVAL_MS = 3000
const POLL_MAX_DURATION_MS = 5 * 60 * 1000  // 5 minutes — Textract async usually finishes in 8-15s

function loadStoredDocs() {
  // Real mode: the server is the source of truth — NEVER hydrate the document
  // list from localStorage. Older builds/demo sessions left a psnap:documents
  // snapshot behind, which painted stale docs for a beat on every load before
  // fetchDocuments replaced them (visible flash on refresh). Also clear the
  // residue so it can't resurface.
  if (!IS_MOCK_MODE) {
    try { localStorage.removeItem(STORAGE_KEYS.documents) } catch { /* private mode etc. */ }
    return []
  }
  const stored = readJSON(STORAGE_KEYS.documents, null)
  // First time the demo user signs in, seed with the four sample docs.
  if (!stored) {
    const seeded = getDemoDocuments()
    writeJSON(STORAGE_KEYS.documents, seeded)
    return seeded
  }
  return stored || []
}

function persistDocs(list) {
  if (IS_MOCK_MODE) writeJSON(STORAGE_KEYS.documents, list)
}

export const useDocumentsStore = defineStore('documents', () => {
  const documents = ref(loadStoredDocs())
  const activeDocument = ref(null)
  const nextToken = ref(null)
  const isLoading = ref(false)
  const isPolling = ref(false)
  const error = ref('')

  // In-memory blob URL cache for files uploaded this session. We don't
  // persist these to localStorage — blob URLs are tied to the document
  // lifecycle and refreshing the page invalidates them. ResultView
  // gracefully falls back to the faux-document renderer when a blob
  // isn't available (e.g. seed docs or after a refresh).
  const blobUrls = new Map()

  const hasMore = computed(() => !!nextToken.value)
  const completedCount = computed(() => documents.value.filter(d => d.status === 'complete').length)

  // ── History filters (client-side in mock mode; Session 8 forwards them
  // as query params on /documents) ─────────────────────────────────────
  const DEFAULT_FILTERS = { search: '', docType: 'all', status: 'all', range: 'all' }
  const filters = ref({ ...DEFAULT_FILTERS })

  const hasActiveFilters = computed(() =>
    filters.value.search !== '' ||
    filters.value.docType !== 'all' ||
    filters.value.status !== 'all' ||
    filters.value.range !== 'all')

  const RANGE_DAYS = { '7d': 7, '30d': 30, '90d': 90 }

  const filteredDocuments = computed(() => {
    const f = filters.value
    const q = f.search.trim().toLowerCase()
    const cutoff = RANGE_DAYS[f.range]
      ? Date.now() - RANGE_DAYS[f.range] * 24 * 60 * 60 * 1000
      : null
    return documents.value.filter((d) => {
      if (q && !(d.filename || '').toLowerCase().includes(q)) return false
      if (f.docType !== 'all' && d.docType !== f.docType) return false
      if (f.status !== 'all' && d.status !== f.status) return false
      if (cutoff && new Date(d.createdAt).getTime() < cutoff) return false
      return true
    })
  })

  function setFilter(key, value) {
    if (key in filters.value) filters.value = { ...filters.value, [key]: value }
  }

  function clearFilters() {
    filters.value = { ...DEFAULT_FILTERS }
  }

  async function fetchDocuments({ append = false } = {}) {
    if (IS_MOCK_MODE) {
      // documents.value is already loaded from localStorage at store init.
      // Re-read in case another tab updated it.
      if (!append) documents.value = readJSON(STORAGE_KEYS.documents, []) || []
      return { items: documents.value, nextToken: null }
    }

    isLoading.value = true
    error.value = ''
    try {
      const { data } = await api.get('/documents', {
        params: nextToken.value && append ? { cursor: nextToken.value } : {},
      })
      documents.value = append ? [...documents.value, ...(data.items || [])] : data.items || []
      nextToken.value = data.nextToken || null
      return data
    } catch (err) {
      error.value = err?.response?.data?.error || err?.message || 'Failed to fetch documents'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function fetchDocument(docId) {
    if (IS_MOCK_MODE) {
      const list = readJSON(STORAGE_KEYS.documents, []) || []
      const doc = list.find(d => d.docId === docId) || null
      activeDocument.value = doc
      return doc
    }

    isLoading.value = true
    error.value = ''
    try {
      const { data } = await api.get(`/documents/${docId}`)
      activeDocument.value = data
      return data
    } catch (err) {
      error.value = err?.response?.data?.error || err?.message || 'Failed to fetch document'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  function setBlobUrl(docId, url) {
    if (!docId || !url) return
    // Clean up any prior URL for this docId.
    const existing = blobUrls.get(docId)
    if (existing) URL.revokeObjectURL(existing)
    blobUrls.set(docId, url)
  }

  function getBlobUrl(docId) {
    return blobUrls.get(docId) || ''
  }

  function revokeBlobUrl(docId) {
    const url = blobUrls.get(docId)
    if (url) {
      URL.revokeObjectURL(url)
      blobUrls.delete(docId)
    }
  }

  function revokeAllBlobUrls() {
    for (const url of blobUrls.values()) URL.revokeObjectURL(url)
    blobUrls.clear()
  }

  // Mock upload: simulates the full Classifier → Extractor → Normalizer
  // pipeline with sensible delays so the 4-stage UploadProgress feels real.
  async function mockUploadAndExtract(file, { docId, onStage } = {}) {
    if (typeof onStage === 'function') onStage(0)
    await new Promise(r => setTimeout(r, 800))      // upload
    if (typeof onStage === 'function') onStage(1)
    await new Promise(r => setTimeout(r, 1500))     // classify
    if (typeof onStage === 'function') onStage(2)
    await new Promise(r => setTimeout(r, 4500))     // extract
    if (typeof onStage === 'function') onStage(3)
    await new Promise(r => setTimeout(r, 700))      // normalise

    const createdAt = new Date().toISOString()
    const doc = pickSampleForFile(file, { docId, createdAt })

    const list = [doc, ...documents.value.filter(d => d.docId !== docId)]
    documents.value = list
    persistDocs(list)
    activeDocument.value = doc
    return doc
  }

  async function pollDocument(docId, { onUpdate } = {}) {
    if (IS_MOCK_MODE) {
      // Real polling isn't needed — mockUploadAndExtract resolves with the
      // final doc directly. This branch supports any unexpected callers.
      const doc = await fetchDocument(docId)
      if (typeof onUpdate === 'function') onUpdate(doc)
      if (doc?.status === 'complete') trackEvent('document_processed')
      return doc
    }

    isPolling.value = true
    const startedAt = Date.now()
    try {
      while (isPolling.value) {
        const doc = await fetchDocument(docId)
        if (typeof onUpdate === 'function') onUpdate(doc)
        if (doc?.status === 'complete' || doc?.status === 'failed') {
          // The core papersnap action — count only genuine successes.
          if (doc?.status === 'complete') trackEvent('document_processed')
          return doc
        }
        if (Date.now() - startedAt > POLL_MAX_DURATION_MS) {
          throw new Error('Processing timed out. Please try again or contact support.')
        }
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS))
      }
    } finally {
      isPolling.value = false
    }
  }

  function stopPolling() {
    isPolling.value = false
  }

  async function deleteDocument(docId) {
    if (IS_MOCK_MODE) {
      const list = documents.value.filter(d => d.docId !== docId)
      documents.value = list
      persistDocs(list)
      if (activeDocument.value?.docId === docId) activeDocument.value = null
      return
    }
    await api.delete(`/documents/${docId}`)
    documents.value = documents.value.filter(d => d.docId !== docId)
    if (activeDocument.value?.docId === docId) activeDocument.value = null
  }

  // Manual correction. Real mode persists into extracted.json via the
  // backend and swaps in the returned document; mock mode edits in place.
  // Fields take `value`; table rows ('table:t:r') take `values` (cells);
  // notes ('note:n') take `value`.
  async function updateField(docId, key, value, values) {
    if (IS_MOCK_MODE) {
      const tableMatch = /^table:(\d+):(\d+)$/.exec(key)
      const noteMatch = /^note:(\d+)$/.exec(key)
      const apply = (doc) => {
        if (!doc || doc.docId !== docId) return doc
        if (tableMatch) {
          const [ti, ri] = [Number(tableMatch[1]), Number(tableMatch[2])]
          const tables = (doc.tables || []).map((t, i) => {
            if (i !== ti) return t
            const norm = Array.isArray(t) ? { title: '', headers: t[0] || [], rows: t.slice(1) } : { ...t }
            const rows = [...(norm.rows || [])]
            rows[ri] = values
            return { ...norm, rows, rowEdited: { ...norm.rowEdited, [ri]: true } }
          })
          return { ...doc, tables }
        }
        if (noteMatch) {
          const ni = Number(noteMatch[1])
          const notes = [...(doc.notes || [])]
          notes[ni] = value
          return { ...doc, notes, noteEdited: { ...doc.noteEdited, [ni]: true } }
        }
        const fields = (doc.fields || []).map((f) =>
          f.key === key ? { ...f, value, confidence: 100, edited: true } : f)
        return { ...doc, fields }
      }
      documents.value = documents.value.map(apply)
      persistDocs(documents.value)
      activeDocument.value = apply(activeDocument.value)
      return activeDocument.value
    }
    const { data } = await api.patch(`/documents/${docId}/fields`, { key, value, values })
    if (activeDocument.value?.docId === docId) activeDocument.value = data
    return data
  }

  // Document-level "did we get this right?" verdict (Quality tab).
  async function qualityFeedback(docId, verdict) {
    if (IS_MOCK_MODE) {
      const apply = (doc) => (doc && doc.docId === docId ? { ...doc, qualityVerdict: verdict } : doc)
      documents.value = documents.value.map(apply)
      persistDocs(documents.value)
      activeDocument.value = apply(activeDocument.value)
      return
    }
    await api.post(`/documents/${docId}/quality-feedback`, { verdict })
    if (activeDocument.value?.docId === docId) activeDocument.value = { ...activeDocument.value, qualityVerdict: verdict }
  }

  // Email a document's extracted JSON to the user. dest: 'registered' | 'custom'.
  // Returns { ok, sentTo }. Real mode only — relies on server-side SES.
  async function emailResult(docId, dest = 'registered') {
    if (IS_MOCK_MODE) {
      return { ok: true, sentTo: 'demo@papersnap.io' }
    }
    const { data } = await api.post(`/documents/${docId}/email-result`, { dest })
    return data
  }

  // Re-run the pipeline on the stored original (real mode only — the mock
  // pipeline has nothing new to say). First rescan per doc is free; later
  // ones count against quota like an upload. focusKeys = fields the user
  // marked incorrect, called out in the re-extraction prompt.
  async function rescanDocument(docId, { pages, consentOverage, focusKeys } = {}) {
    await api.post(`/documents/${docId}/rescan`, { pages, consentOverage, focusKeys })
    return pollDocument(docId)
  }

  // Per-field correctness feedback. verdict: 'correct' | 'incorrect' | null.
  // Synthetic keys: 'line:<i>' (line items), 'table:<t>:<r>' (table rows),
  // 'note:<i>' (narrative notes).
  async function fieldFeedback(docId, key, verdict) {
    if (IS_MOCK_MODE) {
      const setVerdict = (rec) => {
        const next = { ...rec }
        if (verdict) next.feedback = verdict
        else delete next.feedback
        return next
      }
      const setMap = (map, idx) => {
        const next = { ...map }
        if (verdict) next[idx] = verdict
        else delete next[idx]
        return next
      }
      const lineMatch = /^line:(\d+)$/.exec(key)
      const tableMatch = /^table:(\d+):(\d+)$/.exec(key)
      const noteMatch = /^note:(\d+)$/.exec(key)
      const apply = (doc) => {
        if (!doc || doc.docId !== docId) return doc
        if (tableMatch) {
          const [ti, ri] = [Number(tableMatch[1]), Number(tableMatch[2])]
          const tables = (doc.tables || []).map((t, i) => {
            if (i !== ti) return t
            const norm = Array.isArray(t) ? { title: '', headers: t[0] || [], rows: t.slice(1) } : { ...t }
            return { ...norm, rowFeedback: setMap(norm.rowFeedback, ri) }
          })
          return { ...doc, tables }
        }
        if (noteMatch) {
          return { ...doc, noteFeedback: setMap(doc.noteFeedback, Number(noteMatch[1])) }
        }
        if (lineMatch) {
          const idx = Number(lineMatch[1])
          const lineItems = (doc.lineItems || []).map((li, i) => (i === idx ? setVerdict(li) : li))
          return { ...doc, lineItems }
        }
        const fields = (doc.fields || []).map((f) => (f.key === key ? setVerdict(f) : f))
        return { ...doc, fields }
      }
      documents.value = documents.value.map(apply)
      persistDocs(documents.value)
      activeDocument.value = apply(activeDocument.value)
      return activeDocument.value
    }
    const { data } = await api.post(`/documents/${docId}/feedback`, { key, verdict })
    if (activeDocument.value?.docId === docId) activeDocument.value = data
    return data
  }

  function clearActive() {
    activeDocument.value = null
  }

  function clearAll() {
    documents.value = []
    activeDocument.value = null
    revokeAllBlobUrls()
    persistDocs([])
  }

  function reseedDemo() {
    const seeded = getDemoDocuments()
    documents.value = seeded
    persistDocs(seeded)
  }

  return {
    documents,
    activeDocument,
    nextToken,
    isLoading,
    isPolling,
    error,
    hasMore,
    completedCount,
    filters,
    hasActiveFilters,
    filteredDocuments,
    setFilter,
    clearFilters,
    isMockMode: IS_MOCK_MODE,
    fetchDocuments,
    fetchDocument,
    mockUploadAndExtract,
    pollDocument,
    stopPolling,
    deleteDocument,
    updateField,
    fieldFeedback,
    qualityFeedback,
    emailResult,
    rescanDocument,
    clearActive,
    clearAll,
    reseedDemo,
    setBlobUrl,
    getBlobUrl,
    revokeBlobUrl,
    revokeAllBlobUrls,
  }
})
